<script setup lang="ts">
import { computed, inject, nextTick, reactive, ref, watch } from "vue";
import Button from "@/design/primitives/Button.vue";
import Field from "@/design/primitives/Field.vue";
import {
	internalKey,
	recordFields,
	shownAtStage,
	shownWhen,
} from "@/dynamic/resolvers/record-fields";
import { DISPLAY_CONTEXT, type DisplayContext, LOOKUP_PORT } from "@/shared/contracts/ports";
import {
	childrenInParent,
	type EntityDefinition,
	type FieldDefinition,
	type RelationDefinition,
} from "@/shared/contracts/structure";
import { useAutofill } from "./autofill";
import { useCopyFrom } from "./copy-from";
import { resolveDefault } from "./default-token";
import { formatValue } from "./resolvers/field-config";
import {
	describeDerived,
	describeFormula,
	formulaOf,
	isComputedField,
	isFormulaField,
	relationField,
	resolveWidget,
} from "./resolvers/field-widget";
import { evaluateFormula } from "./resolvers/formula";
import { itemNoun } from "./resolvers/item-noun";
import SnippetVariables from "./SnippetVariables.vue";
import { say } from "./say";
import { useChildForms } from "./use-child-forms";
import { validate } from "./widgets/contract";
import SubformWidget from "./widgets/SubformWidget.vue";

/** Render only the fields supplied by the caller; public endpoints authorize them. */
const props = defineProps<{
	entity: EntityDefinition;
	initial?: Record<string, unknown>;
	/**
	 * Um registro NOVO que já nasce começado — `?novo=1&campo.x=…`. Semente, não
	 * edição: não conta como sujeira, e o auto-preenchimento continua a correr
	 * no que ficou vazio (o que `initial` desligaria).
	 */
	defaults?: Record<string, unknown>;
	/** Related records in the native internal form. */
	relations?: readonly RelationDefinition[];
	/** The record being edited; children need something to point at. */
	recordId?: string | null;
	busy?: boolean;
	serverError?: string;
	serverField?: string;
	submitLabel?: string;
	hideCancel?: boolean;
	/** A etapa já tem lugar próprio na tela (o seletor do painel do cartão): não a repete. */
	hideStage?: boolean;
	/** A etapa escolhida FORA do formulário (o "Novo cartão"): é ela que decide os campos de etapa. */
	stage?: string;
	/**
	 * Os campos da tela de formulário do construtor (`fieldCodes`). Dado, o formulário é o
	 * CURTO de quem trabalha: os declarados da lista no topo, o resto em "Mais detalhes", e
	 * uma chave única que nenhuma tela lista (escrita por função) não aparece. Ausente = tudo.
	 */
	layout?: readonly string[] | null;
	/** Só estes campos — a recusa de uma etapa pede o que falta, não o formulário inteiro. */
	only?: readonly string[] | null;
}>();
const emit = defineEmits<{
	submit: [Record<string, unknown>];
	cancel: [];
	/** Há algo a perder aqui dentro. Quem decide o que fazer com o fato é a tela. */
	"update:dirty": [dirty: boolean];
}>();

/**
 * How a computed value is SHOWN. Injected, not a prop.
 *
 * A route component receives only what the URL carries, and the tenant's locale
 * is not in it — the same reasoning `EntityDetailPage` writes out. The fallback
 * is for the rare mount outside the shell and never reaches a real screen.
 */
const FALLBACK_DISPLAY: DisplayContext = {
	locale: "pt-BR",
	currency: "BRL",
	timeZone: "America/Sao_Paulo",
};
const display = inject(
	DISPLAY_CONTEXT,
	computed(() => FALLBACK_DISPLAY),
);

const fields = computed(() => recordFields(props.entity, props.relations));
const byCode = computed(() => new Map(fields.value.map((f) => [f.code, f])));
const childRelations = computed(() => childrenInParent(props.relations ?? [], props.entity.code));

const {
	childForms,
	definitions,
	label: childLabel,
	childDirty,
	openChild,
} = useChildForms(() => childRelations.value);

const values = reactive<Record<string, unknown>>({});
const touched = reactive<Record<string, boolean>>({});

/** O valor com que um campo abre: o gravado, senão o pedido, senão o de fábrica. */
function seedOf(field: (typeof fields.value)[number]): unknown {
	const seed = props.initial?.[field.code] ?? props.defaults?.[field.code] ?? defaultFor(field);
	// Um registro novo de funil nunca abre com a etapa vazia ("—"): o servidor o põe na primeira.
	return field.code === "stage" && !seed && !props.initial?.id ? defaultFor(field) : seed;
}

watch(
	// `defaults` pelo VALOR: um objeto novo com o mesmo conteúdo não reescreve o que se digitou.
	[() => props.initial, () => props.entity, () => JSON.stringify(props.defaults ?? {})],
	() => {
		for (const field of fields.value) values[field.code] = seedOf(field);
	},
	{ immediate: true },
);

// Auto-preenchimento (§2.6 #16). A regra inteira mora em `autofill.ts`: o que a
// plataforma já sabe não é assunto do renderizador, e este arquivo já é grande.
useAutofill({ entity: () => props.entity, values, initial: () => props.initial });
// `config.copyFrom`: escolher o vínculo traz o valor dele na hora (o servidor repete no salvar).
const copiedFrom = useCopyFrom({
	entity: () => props.entity,
	values,
	initial: () => props.initial,
	factory: (code) => {
		const field = byCode.value.get(code);
		return field ? defaultFor(field) : undefined;
	},
});

/**
 * Algo a perder: um campo difere do que o formulário recebeu ao abrir.
 *
 * Contra a MESMA semente do watch acima (`seedOf`), normalizado por
 * JSON — um form recém-aberto, com seus "" e [] de fábrica, não é sujeira. É o fato
 * que deixa Esc e clique-fora perguntarem antes de descartar, em vez de engolir dez
 * campos digitados em silêncio.
 */
const dirty = computed(
	() =>
		childDirty.value ||
		fields.value.some((field) => {
			const seed = seedOf(field);
			return JSON.stringify(values[field.code] ?? null) !== JSON.stringify(seed ?? null);
		}),
);
watch(dirty, (value) => emit("update:dirty", value), { immediate: true });

/**
 * O 1:1 dito ANTES do salvar: para cada campo de referência que é chave única
 * de um campo só, o formulário pede os valores já tomados e o seletor deixa de
 * oferecer quem já pertence a outro registro. O dono continua podendo o SEU —
 * em edição, o valor do próprio registro sai do conjunto. A verdade segue sendo
 * o índice; isto é o seletor sendo honesto de antemão.
 */
const lookup = inject(LOOKUP_PORT, null);
const takenByField = ref(new Map<string, Set<string>>());
watch(
	[() => props.entity, () => props.recordId],
	async () => {
		const unique = (props.entity.uniqueKeys ?? [])
			.filter((key) => key.length === 1)
			.map((key) => key[0] as string)
			.filter((code) => {
				const field = props.entity.fields.find((f) => f.code === code && !f.archived);
				return field?.type.startsWith("ref_") ?? false;
			});
		if (unique.length === 0) {
			takenByField.value = new Map();
			return;
		}
		const taken = lookup?.takenValues;
		if (!taken) {
			// A porta pública não tem o método — degradação honesta: o seletor
			// oferece tudo e o índice recusa no salvar.
			takenByField.value = new Map();
			return;
		}
		const next = new Map<string, Set<string>>();
		await Promise.all(
			unique.map(async (code) => {
				try {
					const rows = await taken({ entity: props.entity.code, field: code });
					next.set(
						code,
						new Set(
							rows
								.filter(
									(t: { recordId: string }) => !props.recordId || t.recordId !== props.recordId,
								)
								.map((t: { value: string }) => t.value),
						),
					);
				} catch {
					// Idem: sem a lista, o índice continua sendo a verdade.
				}
			}),
		);
		takenByField.value = next;
	},
	{ immediate: true },
);

function defaultFor(field: FieldDefinition): unknown {
	if (field.code === "stage") return props.entity.process?.stages[0]?.code ?? "";
	// O padrão que o próprio campo declara — o mesmo que o servidor aplica no que não veio.
	const fallback = resolveDefault(field.config?.default, {
		now: new Date(),
		timeZone: display.value.timeZone,
		me: display.value.viewer,
		editing: Boolean(props.initial?.id),
	});
	if (fallback !== undefined) return fallback;
	if (field.type === "boolean") return false;
	if (field.type === "multi_select") return [];
	// null rather than "": a nullable numeric column must stay null, and an empty
	// string is not a number.
	if (field.type === "number" || field.type === "integer" || field.type === "currency") return null;
	return "";
}

/**
 * "Mostrar só nas etapas" e "mostrar só quando": fora delas o campo não aparece nem é
 * cobrado. `values` é reativo — trocar a Finalidade mostra e esconde na hora.
 */
function atItsStage(field: FieldDefinition): boolean {
	return (
		shownAtStage(field, props.entity, props.stage ?? values.stage) &&
		shownWhen(field, props.entity, values)
	);
}

const creating = computed(() => !props.initial?.id);

/**
 * O que o formulário desenha: menos a etapa quando a tela já a mostra, o calculado num
 * registro que ainda não existe (não há o que calcular) e a chave interna.
 */
const drawn = computed(() =>
	fields.value.filter(
		(field) =>
			atItsStage(field) &&
			!(props.hideStage && field.code === "stage") &&
			!(props.only && !props.only.includes(field.code)) &&
			!(creating.value && isComputedField(field)) &&
			!internalKey(field, props.layout),
	),
);

const activeFields = computed(() => drawn.value.filter((field) => !isComputedField(field)));

/**
 * O TOPO do formulário curto: o que a tela declarou, a lista mostra ou é obrigatório — e
 * que não nasce preenchido sozinho (um padrão, a etapa que começa na primeira). O resto fica
 * em "Mais detalhes", fechado; dois campos não valem a dobra.
 */
function onTop(field: FieldDefinition): boolean {
	return eligible(field) && (asked(field) || followsTop(field));
}
function eligible(field: FieldDefinition): boolean {
	const declared = !props.layout?.length || props.layout.includes(field.code);
	const filledByItself = field.code === "stage" || field.config?.default != null;
	return declared && !filledByItself;
}
const asked = (field: FieldDefinition) => field.showInList || field.required;
/** O que aparece por uma escolha do topo (`showWhen`) aparece junto dela, não na dobra. */
function followsTop(field: FieldDefinition): boolean {
	const code = (field.config?.showWhen as { field?: unknown } | undefined)?.field;
	const control = typeof code === "string" ? byCode.value.get(code) : undefined;
	return Boolean(control && eligible(control) && asked(control));
}
const split = computed(() => {
	const top = drawn.value.filter(onTop);
	const more = drawn.value.filter((field) => !onTop(field));
	return props.layout && !props.only && top.length > 0 && more.length > 2
		? { top, more }
		: { top: drawn.value, more: [] };
});
const moreOpen = ref(false);

/**
 * A computed value, formatted for reading. Never an input.
 *
 * A FORMULA recalculates AS THE PERSON TYPES: the mirror evaluator runs over the
 * live `values`, so the subtotal moves with the quantity — the spreadsheet
 * feeling, honestly earned because the server compiles the same grammar. When
 * the mirror cannot compute (a newer grammar, say), the stored value stands:
 * the preview is never louder than the data. A derived aggregate has no live
 * half — its inputs are other ROWS — so it reads what the record brought.
 */
function readComputed(field: FieldDefinition): string {
	const expr = formulaOf(field);
	const live = expr ? evaluateFormula(expr, values) : undefined;
	const value = formatValue(
		live === undefined ? values[field.code] : live,
		field.type,
		display.value,
		field.config,
	);
	return value === "" ? "—" : value;
}

/**
 * A refusal nobody on this form can display.
 *
 * The banner used to appear only when the server named NO field, so a message
 * blaming a field this particular form does not draw — a total on the parent,
 * refused by an invariant while a child was being saved — vanished entirely and
 * the form simply did nothing. Anything without a home on screen belongs here.
 */
const orphanError = computed(() => {
	if (!props.serverError) return "";
	if (!props.serverField) return props.serverError;
	const field = byCode.value.get(props.serverField);
	if (!field || isComputedField(field)) return props.serverError;
	return activeFields.value.some((f) => f.code === props.serverField) ? "" : props.serverError;
});

function problemFor(field: FieldDefinition): string {
	if (props.serverField === field.code && props.serverError) return props.serverError;
	if (!touched[field.code]) return "";
	return validate(values[field.code], field) ?? "";
}

const canSubmit = computed(() =>
	activeFields.value.every((f) => validate(values[f.code], f) === null),
);

/**
 * Submits only what is VISIBLE.
 *
 * A hidden field is neither validated nor sent (§6). Sending it anyway would
 * record an answer the person never gave — and after they hid it by changing
 * another field, an answer they explicitly took back.
 */
function submit(): void {
	for (const field of activeFields.value) touched[field.code] = true;
	if (props.busy) return;
	const children = canSubmit.value ? childForms.value.map((form) => form.collect()) : [null];
	if (children.some((items) => items === null)) {
		moreOpen.value ||= split.value.more.some((f) => validate(values[f.code], f) !== null);
		showFirstProblem();
		return;
	}

	const payload: Record<string, unknown> = {};
	// A etapa só viaja quando muda: num registro novo, quando não é a primeira — o servidor
	// cria nela e o move para a escolhida, com o histórico de uma troca de etapa.
	const stageBefore = props.initial?.id
		? props.initial.stage
		: props.entity.process?.stages[0]?.code;
	for (const field of activeFields.value) {
		if (field.code === "stage" && values.stage === stageBefore) continue;
		payload[field.code] = values[field.code];
	}
	if (children.flat().length) payload.$children = children.flat();
	emit("submit", payload);
}

/**
 * A recusa onde a pessoa está olhando: o botão mora no FIM do formulário, e o
 * campo errado quase sempre lá em cima — o erro pintava fora da vista (QA 19/10).
 * Por isso o "Criar" não nasce desligado: o clique é o que leva até o que falta.
 */
const formEl = ref<HTMLFormElement | null>(null);
function showFirstProblem(): void {
	void nextTick(() => {
		// O controle marcado; senão a mensagem (um item filho, a quantidade mínima).
		const first =
			formEl.value?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
			formEl.value?.querySelector<HTMLElement>("p.text-danger");
		first?.scrollIntoView?.({ block: "center" });
		first?.focus({ preventScroll: true });
	});
}

/**
 * Num registro NOVO, os itens de outras tabelas que apontam para ele (os filhos)
 * ficam fechados: são opcionais, e o formulário aberto de cada um — com o
 * "Contato *" dele — parecia uma exigência do registro que se está criando.
 */
// A recusa do servidor também: o campo que ela nomeia, ou a faixa no fim. `immediate`
// porque um formulário pode já NASCER de uma recusa (a etapa que uma regra barrou).
watch(
	() => props.serverError,
	(error) => {
		if (!error) return;
		moreOpen.value ||= split.value.more.some((f) => f.code === props.serverField);
		showFirstProblem();
	},
	{ immediate: true },
);

const openedChildren = reactive(new Set<string>());
const childShown = (code: string) => Boolean(props.recordId) || openedChildren.has(code);
/**
 * Os filhos que o formulário oferece. Num registro novo, UMA porta só — o primeiro que o
 * construtor declarou (relação ou `tabOrder`); o resto se adiciona na ficha depois de
 * salvo. Doze "+ Adicionar … agora" num cadastro eram doze perguntas.
 */
const offeredChildren = computed(() => {
	if (props.only) return [];
	if (props.recordId) return childRelations.value;
	const first = childRelations.value.find((r) => r.source !== "field" || r.tabOrder !== undefined);
	return first ? [first] : [];
});
</script>

<template>
	<form ref="formEl" class="space-y-4" @submit.prevent="submit">
		<div class="space-y-4">
			<template v-for="field in [...split.top, ...(moreOpen ? split.more : [])]" :key="field.code">
				<div v-if="isComputedField(field)" class="space-y-1.5">
					<span class="block text-xs font-medium text-fg-muted">{{ field.label }}</span>
					<p class="text-sm text-fg tabular-nums">{{ readComputed(field) }}</p>
					<p class="text-xs text-fg-subtle">{{ field.helpText || (isFormulaField(field) ? describeFormula(field, fields) : describeDerived(field, childRelations.map((r) => ({ toEntityCode: r.toEntityCode, label: childLabel(r) })))) }}</p>
				</div>
				<Field v-else v-slot="{ id, describedBy }" :label="field.label" :required="field.required" :hint="field.helpText ?? (copiedFrom[field.code] ? say('copiedFrom', { link: copiedFrom[field.code] }) : undefined)" :error="problemFor(field)">
					<component :is="resolveWidget(field)" :field="field" :model-value="values[field.code]" :disabled="busy" :error="problemFor(field)" :owner-id="recordId ?? undefined" :taken="takenByField.get(field.code)" :control-id="id" :described-by="describedBy" @update:model-value="values[field.code] = $event" @blur="touched[field.code] = true" />
					<SnippetVariables v-if="entity.role === 'composer.snippet' && field.code === 'text'" :model-value="values[field.code]" :control-id="id" :disabled="busy" @update:model-value="values[field.code] = $event" />
				</Field>
			</template>
			<button v-if="split.more.length && !moreOpen" type="button" class="flex items-center gap-1 text-xs font-medium text-accent hover:underline" :aria-expanded="false" @click="moreOpen = true">{{ say("moreDetails") }} ({{ split.more.length }})</button>
			<div v-for="relation in offeredChildren" :key="relation.code" class="min-w-0 space-y-1.5">
				<button v-if="!childShown(relation.code)" type="button" class="text-xs font-medium text-accent hover:underline" :disabled="busy" @click="openedChildren.add(relation.code)">{{ say("addChildren", { name: itemNoun(childLabel(relation), display.locale) }) }}</button>
				<span v-else class="block text-xs font-medium text-fg-muted">{{ childLabel(relation) }}</span>
				<SubformWidget v-if="childShown(relation.code)" ref="childForms" :draft-mode="!recordId" :child-definition="definitions[relation.toEntityCode]" :field="relationField({ code: relation.code, label: relation.label, toEntityCode: relation.toEntityCode, required: relation.required })" :model-value="null" :disabled="busy" :child-entity="relation.toEntityCode" :parent-field="relation.fieldCode" :parent-entity="entity.code" :parent-id="recordId ?? null" @open="openChild(relation.toEntityCode, $event)" />
			</div>
		</div>
		<p v-if="orphanError" class="rounded-xl bg-danger-subtle px-3 py-2 text-xs text-danger">{{ orphanError }}</p>
		<!-- O que a tela tem a dizer sobre o "Criar" mora junto dele, não no topo (QA round 8). -->
		<slot name="beforeActions" />
		<div class="flex gap-2 pt-1">
			<Button type="submit" :loading="busy">{{ submitLabel ?? say("save") }}</Button>
			<Button v-if="!hideCancel" variant="ghost" type="button" @click="emit('cancel')">{{ say("cancel") }}</Button>
		</div>
	</form>
</template>
